// The entity/component substrate. Entities are IDs; components are typed records owned
// by modules; systems (module tick logic) query components. This is the ECS *pattern*
// adopted per kernel-substrate-and-ecs.md — backed by the simplest possible storage
// (plain Maps) behind a swappable `World` interface, with no ECS-framework dependency.
// The component/query surface here is the eventual modding ABI, so it stays owned.

export type EntityId = number;

/** Component identity is a plain string. Modules namespace their own (e.g.
 *  "spatial-movement/BoxCollider"); kernel-owned components use bare names
 *  ("Transform"). The store is agnostic to the convention. */
export type ComponentType = string;

export interface World {
  createEntity(): EntityId;
  destroyEntity(id: EntityId): void;
  hasEntity(id: EntityId): boolean;

  addComponent<T>(id: EntityId, type: ComponentType, data: T): void;
  getComponent<T>(id: EntityId, type: ComponentType): T | undefined;
  hasComponent(id: EntityId, type: ComponentType): boolean;
  removeComponent(id: EntityId, type: ComponentType): void;

  /** Entities that currently have *all* of the given component types. */
  query(...types: ComponentType[]): EntityId[];
  /** Every (entity, data) pair for a single component type. The serialization path. */
  componentEntries<T>(type: ComponentType): Array<[EntityId, T]>;
  /** Every component type with at least one entity, in first-use order (inspectors and tools; optional for other
   *  stores). */
  componentTypes?(): ComponentType[];
}

/** Map-backed `World`. Inspectable, trivially serializable, near-zero mental-model
 *  shift from plain typed shapes. The archetype/SoA storage decision is deferred to
 *  when the combat module needs it (kernel-substrate-and-ecs.md). */
export class MapWorld implements World {
  private nextId = 1;
  private readonly entities = new Set<EntityId>();
  // componentType -> (entityId -> data)
  private readonly stores = new Map<ComponentType, Map<EntityId, unknown>>();

  createEntity(): EntityId {
    const id = this.nextId++;
    this.entities.add(id);
    return id;
  }

  destroyEntity(id: EntityId): void {
    this.entities.delete(id);
    for (const store of this.stores.values()) store.delete(id);
  }

  hasEntity(id: EntityId): boolean {
    return this.entities.has(id);
  }

  addComponent<T>(id: EntityId, type: ComponentType, data: T): void {
    if (!this.entities.has(id)) {
      throw new Error(`World: cannot add "${type}" to unknown entity ${id}`);
    }
    let store = this.stores.get(type);
    if (!store) {
      store = new Map();
      this.stores.set(type, store);
    }
    store.set(id, data);
  }

  getComponent<T>(id: EntityId, type: ComponentType): T | undefined {
    return this.stores.get(type)?.get(id) as T | undefined;
  }

  hasComponent(id: EntityId, type: ComponentType): boolean {
    return this.stores.get(type)?.has(id) ?? false;
  }

  removeComponent(id: EntityId, type: ComponentType): void {
    this.stores.get(type)?.delete(id);
  }

  query(...types: ComponentType[]): EntityId[] {
    if (types.length === 0) return [...this.entities];
    // Iterate the smallest store first so the intersection stays cheap.
    const stores = types.map((t) => this.stores.get(t));
    if (stores.some((s) => !s)) return [];
    stores.sort((a, b) => a!.size - b!.size);
    const [smallest, ...rest] = stores as Map<EntityId, unknown>[];
    const result: EntityId[] = [];
    for (const id of smallest!.keys()) {
      if (rest.every((s) => s!.has(id))) result.push(id);
    }
    return result;
  }

  componentEntries<T>(type: ComponentType): Array<[EntityId, T]> {
    const store = this.stores.get(type);
    if (!store) return [];
    return [...store.entries()] as Array<[EntityId, T]>;
  }

  componentTypes(): ComponentType[] {
    return [...this.stores.entries()].filter(([, s]) => s.size > 0).map(([t]) => t);
  }
}
